import React, { useEffect, useState } from 'react'
import { MessagesSquare, Wrench, AlertTriangle, Info, PauseCircle } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Input, Button } from '../components/ui'

const tipeMeta = {
  masuk: { label: 'Pelanggan', tone: 'neutral' },
  keluar: { label: 'AI', tone: 'brand' },
  keluar_manual: { label: 'Admin', tone: 'success' },
  tool: { label: 'Tool', tone: 'warning' },
  sistem: { label: 'Sistem', tone: 'neutral' },
  error: { label: 'Error', tone: 'danger' }
}

function waktu(ts) {
  if (!ts) return ''
  return ts.substring(11, 16)
}

function waktuPause(ms) {
  if (!ms) return ''
  return new Date(ms).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' })
}

export default function LogChatAI() {
  const [list, setList] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(null)
  const [thread, setThread] = useState(null)
  const [loadingThread, setLoadingThread] = useState(false)

  useEffect(() => {
    loadList()
    const interval = setInterval(loadList, 8000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!active) return
    loadThread(active)
    const interval = setInterval(() => loadThread(active), 5000)
    return () => clearInterval(interval)
  }, [active])

  async function loadList() {
    try {
      const res = await api.get('/ai-chat-log/percakapan')
      setList(res.data)
      if (!active && res.data.length > 0) setActive(res.data[0].noHp)
    } finally { setLoadingList(false) }
  }

  async function loadThread(noHp) {
    setLoadingThread(true)
    try {
      const res = await api.get(`/ai-chat-log/${encodeURIComponent(noHp)}`)
      setThread(res.data)
    } finally { setLoadingThread(false) }
  }

  async function resumeAi(noHp) {
    await api.post(`/ai-chat-log/${encodeURIComponent(noHp)}/resume`)
    loadList()
    if (active === noHp) loadThread(noHp)
  }

  const filtered = q.trim()
    ? list.filter(c => `${c.noHp} ${c.pppUsername || ''} ${c.nama || ''}`.toLowerCase().includes(q.toLowerCase()))
    : list

  return (
    <div>
      <PageHeader title="Log Chat AI" subtitle="Pantau percakapan chatbot AI dengan pelanggan — pesan masuk, tool yang dipanggil, dan balasan" />

      <div className="grid-sidebar-300" style={{ gap: 16, alignItems: 'start' }}>
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari nomor/nama..." />
          </div>
          <div style={{ maxHeight: 620, overflowY: 'auto' }}>
            {loadingList ? (
              <EmptyState icon={MessagesSquare} title="Memuat..." />
            ) : filtered.length === 0 ? (
              <EmptyState icon={MessagesSquare} title="Belum ada percakapan" />
            ) : filtered.map(c => (
              <div key={c.noHp} onClick={() => setActive(c.noHp)}
                style={{
                  padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: active === c.noHp ? 'var(--brand-soft)' : 'transparent'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{c.isAdmin ? (c.adminNama || 'CS Admin') : (c.nama || c.pppUsername || c.noHp)}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-faint)' }}>{waktu(c.lastCreatedAt)}</div>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {c.isAdmin ? <Badge tone="success" style={{ marginRight: 4 }}>CS Admin</Badge> : null}
                  {c.isAdmin ? c.noHp : (c.pppUsername || 'Belum terdaftar')} · {c.jumlahPesan} pesan
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.lastTipe === 'tool' ? '🔧 ' : c.lastTipe === 'error' ? '⚠️ ' : ''}{c.lastIsi}
                </div>
                {c.pausedUntil && (
                  <Badge tone="warning" style={{ marginTop: 6 }}><PauseCircle size={11} /> AI dijeda s/d {waktuPause(c.pausedUntil)}</Badge>
                )}
              </div>
            ))}
          </div>
        </Card>

        <Card style={{ padding: 20, minHeight: 400 }}>
          {!active ? (
            <EmptyState icon={MessagesSquare} title="Pilih percakapan di sebelah kiri" />
          ) : loadingThread && !thread ? (
            <EmptyState icon={MessagesSquare} title="Memuat percakapan..." />
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {thread?.isAdmin ? (thread.adminNama || 'CS Admin') : (thread?.pelanggan?.nama || thread?.pelanggan?.ppp_username || active)}
                    {thread?.isAdmin && <Badge tone="success">CS Admin</Badge>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {active} {thread?.isAdmin ? '· Nomor admin/CS terverifikasi' : thread?.pelanggan?.ppp_username ? `· ${thread.pelanggan.ppp_username}` : '· Belum terdaftar sebagai pelanggan'}
                  </div>
                </div>
                {thread?.pausedUntil && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Badge tone="warning"><PauseCircle size={11} /> AI dijeda s/d {waktuPause(thread.pausedUntil)}</Badge>
                    <Button size="sm" variant="ghost" onClick={() => resumeAi(active)}>Aktifkan AI</Button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(thread?.pesan || []).map(p => <Bubble key={p.id} pesan={p} />)}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

function Bubble({ pesan }) {
  const meta = tipeMeta[pesan.tipe] || tipeMeta.sistem

  if (pesan.tipe === 'masuk' || pesan.tipe === 'keluar' || pesan.tipe === 'keluar_manual') {
    const isCustomer = pesan.tipe === 'masuk'
    const isManual = pesan.tipe === 'keluar_manual'
    return (
      <div style={{ display: 'flex', justifyContent: isCustomer ? 'flex-start' : 'flex-end' }}>
        <div style={{
          maxWidth: '72%', padding: '9px 13px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.45,
          background: isCustomer ? '#f1f1f4' : isManual ? 'var(--success-soft)' : 'var(--brand-soft)',
          color: isCustomer ? 'var(--text)' : isManual ? 'var(--success)' : 'var(--brand-soft-text)'
        }}>
          {isManual && <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, marginBottom: 2 }}>ADMIN (MANUAL)</div>}
          <div style={{ whiteSpace: 'pre-wrap' }}>{pesan.isi}</div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: isCustomer ? 'left' : 'right' }}>{waktu(pesan.created_at)}</div>
        </div>
      </div>
    )
  }

  const Icon = pesan.tipe === 'tool' ? Wrench : pesan.tipe === 'error' ? AlertTriangle : Info
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, maxWidth: '85%', padding: '6px 12px', borderRadius: 10, background: '#fafafc', border: '1px solid var(--border)' }}>
        <Icon size={13} style={{ marginTop: 2, flexShrink: 0, color: 'var(--text-faint)' }} />
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: pesan.tipe === 'tool' ? 'monospace' : 'inherit' }}>
          <Badge tone={meta.tone} style={{ marginRight: 6 }}>{meta.label}</Badge>
          {pesan.isi}
        </div>
      </div>
    </div>
  )
}
